import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import onConversationOptionSaved from 'owa-options-actions/lib/onConversationOptionSaved';
import { onListViewTypeOptionSaved } from 'owa-options-actions/lib/onListViewTypeOptionSaved';
import type ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import type { UpdateUserConfigurationEntry } from 'owa-service/lib/types/UpdateUserConfigurationEntry';
import {
    getUserConfiguration,
    lazyUpdateUserConfigurationService,
    updateUserConfiguration,
} from 'owa-session-store';
import store from '../store/store';
import saveReadingPaneConversationOption from './saveReadingPaneConversationOption';

async function saveConversationsOption(
    newListViewType: ReactListViewType,
    conversationSortOrder: ConversationSortOrder,
    hideDeletedItems: boolean | null,
    readingPaneConversationsEnabled: boolean
) {
    const userOptions = getUserConfiguration().UserOptions;
    let serviceUpdates: UpdateUserConfigurationEntry<'OWA.UserOptions'>[] = [];

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2532 (28,57): Object is possibly 'undefined'.
    // @ts-expect-error
    if (newListViewType !== null && newListViewType !== userOptions.GlobalListViewTypeReact) {
        updateUserConfiguration(config => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (33,13): Object is possibly 'undefined'.
            // @ts-expect-error
            config.UserOptions.GlobalListViewTypeReact = newListViewType;
        });
        serviceUpdates.push({
            key: 'GlobalListViewTypeReact',
            valuetype: 'Integer32',
            value: [`${newListViewType}`],
        });
    }

    onListViewTypeOptionSaved(newListViewType);

    if (
        isFeatureEnabled('mon-rp-unstackedConversation') &&
        readingPaneConversationsEnabled != null
    ) {
        // Save the new option
        await saveReadingPaneConversationOption(readingPaneConversationsEnabled);
    }

    if (conversationSortOrder) {
        updateUserConfiguration(config => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (57,13): Object is possibly 'undefined'.
            // @ts-expect-error
            config.UserOptions.ConversationSortOrderReact = conversationSortOrder;
        });

        // The setting is actually stored as an int on the server, despite being sent down as a string
        // Copied from //depot/utah/sources/dev/clients/src/owa/bin/core/ConversationSortOrderReact.cs
        let sortOrderValue;
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (66,17): Object is possibly 'undefined'.
        // @ts-expect-error
        switch (userOptions.ConversationSortOrderReact) {
            case 'Chronological':
                sortOrderValue = 0x1;
                break;
            case 'Tree':
                sortOrderValue = 0x2;
                break;
            case 'NewestOnTop':
                sortOrderValue = 0x4;
                break;
            case 'NewestOnBottom':
                sortOrderValue = 0x8;
                break;
            case 'ChronologicalNewestOnTop':
                sortOrderValue = 0x5;
                break;
            case 'ChronologicalNewestOnBottom':
                sortOrderValue = 0x9;
                break;
            case 'TreeNewestOnBottom':
                sortOrderValue = 0xa;
                break;
        }

        serviceUpdates.push({
            key: 'ConversationSortOrderReact',
            valuetype: 'Integer32',
            value: [`${sortOrderValue}`],
        });
    }

    if (hideDeletedItems !== null) {
        updateUserConfiguration(config => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (102,13): Object is possibly 'undefined'.
            // @ts-expect-error
            config.UserOptions.HideDeletedItems = hideDeletedItems;
        });
        serviceUpdates.push({
            key: 'HideDeletedItems',
            valuetype: 'Boolean',
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (110,24): Object is possibly 'undefined'.
            // @ts-expect-error
            value: [`${userOptions.HideDeletedItems}`],
        });
    }

    onConversationOptionSaved();

    if (serviceUpdates.length) {
        await lazyUpdateUserConfigurationService.importAndExecute(serviceUpdates);
    }
}

export async function saveConversationsOptionFull() {
    return saveConversationsOption(
        store.globalListViewType,
        store.conversationSortOrder,
        store.hideDeletedItems,
        store.readingPaneConversationEnabled
    );
}

const OPTION_QUICK_SETTING_USAGE_KEY = 'QuickSettingConversations';
const OPTION_PROGRAMMATIC_USAGE_KEY = 'ProgrammaticConversations';

export async function saveConversationsOptionQuick(
    listViewType: ReactListViewType,
    readingPaneConversationEnabled?: boolean,
    newSortOrder?: ConversationSortOrder
) {
    logUsage(OPTION_QUICK_SETTING_USAGE_KEY, [newSortOrder]);

    const hideDeletedItems = null;
    await saveConversationsOption(
        listViewType,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (146,9): Argument of type 'ConversationSortOrder | undefined' is not assignable to parameter of type 'ConversationSortOrder'.
        // @ts-expect-error
        newSortOrder,
        hideDeletedItems,
        readingPaneConversationEnabled
    );
}

export async function saveConversationsOptions(
    listViewType: ReactListViewType,
    readingPaneConversationEnabled?: boolean,
    newSortOrder?: ConversationSortOrder
) {
    logUsage(OPTION_PROGRAMMATIC_USAGE_KEY, [newSortOrder]);

    const hideDeletedItems = null;
    await saveConversationsOption(
        listViewType,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (165,9): Argument of type 'ConversationSortOrder | undefined' is not assignable to parameter of type 'ConversationSortOrder'.
        // @ts-expect-error
        newSortOrder,
        hideDeletedItems,
        readingPaneConversationEnabled
    );
}
